
import { useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { useSermonPlans } from "@/hooks/use-sermon-plans";
import { useWelcomeDialog } from "./use-welcome-dialog";
import { useSermonForm } from "./use-sermon-form";
import { useSermonDeletion } from "./use-sermon-deletion";
import { useAuthentication } from "./use-authentication";
import { SermonFormData } from "./types";

export function useSermonPlansPage() {
  const { toast } = useToast();
  const {
    sermonPlans,
    congregations,
    loading,
    nextAutoGenerationDate,
    isOffline,
    createSermonPlan,
    updateSermonPlan,
    deleteSermonPlan,
    runAutoGeneration,
    fetchData
  } = useSermonPlans();
  
  // Use the refactored hooks
  const { isAuthenticated } = useAuthentication(fetchData);
  
  const {
    formOpen,
    currentSermonPlan,
    setFormOpen,
    handleOpenForm,
    handleFormSubmit
  } = useSermonForm(createSermonPlan, updateSermonPlan, fetchData);
  
  const { handleDelete } = useSermonDeletion(deleteSermonPlan, fetchData);
  
  const {
    showWelcomeDialog,
    showAIDialog,
    activeTab,
    setShowWelcomeDialog,
    setShowAIDialog,
    setActiveTab,
    handleCreateFirstSermon,
    generateAISermon
  } = useWelcomeDialog();

  // Show welcome dialog when no sermon plans exist and user is authenticated
  useEffect(() => {
    if (!loading && isAuthenticated && sermonPlans.length === 0) {
      setShowWelcomeDialog(true);
    }
  }, [loading, sermonPlans, isAuthenticated, setShowWelcomeDialog]);

  // Ensure page is scrolled to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleCreateSermonFromWelcome = (formData?: SermonFormData) => {
    setShowWelcomeDialog(false);
    
    if (activeTab === "ai") {
      setShowAIDialog(true);
    } else if (formData) {
      // Create the sermon directly using the form data from the welcome dialog
      createSermonPlan(formData).then(success => {
        if (success) {
          toast({
            title: "Success",
            description: "New sermon plan created successfully",
          });
          fetchData();
        }
      });
    } else {
      // Fallback to opening the form
      handleOpenForm();
    }
  };

  return {
    sermonPlans,
    congregations,
    loading,
    isAuthenticated,
    isOffline,
    fetchError: null, // We'll get this from useSermonPlansFetch later
    nextAutoGenerationDate,
    formOpen,
    currentSermonPlan,
    showWelcomeDialog,
    showAIDialog,
    activeTab,
    setFormOpen,
    setShowWelcomeDialog,
    setShowAIDialog,
    setActiveTab,
    handleOpenForm,
    handleFormSubmit,
    handleDelete,
    handleCreateFirstSermon: (formData?: SermonFormData) => handleCreateSermonFromWelcome(formData),
    generateAISermon: () => generateAISermon(createSermonPlan, fetchData),
    runAutoGeneration,
    fetchData
  };
}
